<template>
  <teleport to="body">
    <transition name="app-modal-fade">
      <div
        v-if="is_open"
        class="app-modal"
        role="presentation"
        @click.self="handleOverlayClose"
      >
        <div
          class="app-modal__dialog"
          :class="dialogClasses"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? titleId : undefined"
          @click.stop
        >
          <header v-if="title || $slots.header" class="app-modal__header">
            <slot name="header">
              <h3 :id="titleId" class="app-modal__title bottom0">{{ title }}</h3>
            </slot>
            <button
              type="button"
              class="app-modal__close"
              aria-label="Schließen"
              @click="handleClose"
            >
              ×
            </button>
          </header>

          <div class="app-modal__body">
            <slot name="content">
              <slot />
            </slot>
          </div>

          <footer v-if="hasFooter" class="app-modal__footer">
            <slot name="footer">
              <slot name="actions" />
            </slot>
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { computed, watch, onMounted, onUnmounted, useSlots } from 'vue'

const SIZE_ALIASES = {
  half: 'lg',
  medium: 'md',
  large: 'lg',
  small: 'sm'
}

export default {
  emits: ['onClose', 'close'],
  props: {
    is_open: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md'
    },
    auto_height: {
      type: Boolean,
      default: false
    },
    close_on_overlay: {
      type: Boolean,
      default: false
    },
    close_on_escape: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const slots = useSlots()
    const titleId = `app-modal-title-${Math.random().toString(36).slice(2, 9)}`

    const normalizedSize = computed(() => {
      const value = (props.size || 'md').toLowerCase()
      return SIZE_ALIASES[value] || value
    })

    const dialogClasses = computed(() => ({
      [`app-modal__dialog--${normalizedSize.value}`]: true,
      'app-modal__dialog--auto-height': props.auto_height
    }))

    const hasFooter = computed(() => Boolean(slots.footer || slots.actions))

    const handleClose = () => {
      emit('onClose')
      emit('close')
    }

    const handleOverlayClose = () => {
      if (props.close_on_overlay) {
        handleClose()
      }
    }

    const onKeydown = (event) => {
      if (event.key === 'Escape' && props.is_open && props.close_on_escape) {
        handleClose()
      }
    }

    watch(
      () => props.is_open,
      (isOpen) => {
        document.body.style.overflow = isOpen ? 'hidden' : ''
      }
    )

    onMounted(() => {
      document.addEventListener('keydown', onKeydown)
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', onKeydown)
      document.body.style.overflow = ''
    })

    return {
      titleId,
      dialogClasses,
      hasFooter,
      handleClose,
      handleOverlayClose
    }
  }
}
</script>

<style scoped lang="scss">
.app-modal {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.55);
}

.app-modal__dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 90vh;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.app-modal__dialog--sm {
  max-width: 480px;
}

.app-modal__dialog--md {
  max-width: 640px;
}

.app-modal__dialog--lg {
  max-width: 760px;
}

.app-modal__dialog--full {
  max-width: min(95vw, 1200px);
  width: 100%;
  max-height: 95vh;
}

.app-modal__dialog--auto-height {
  height: auto;
}

.app-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
  flex-shrink: 0;
}

.app-modal__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2f2f2f;
  line-height: 1.3;
}

.app-modal__close {
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  color: #777;
  padding: 0;
  flex-shrink: 0;
  transition: color 0.2s ease;

  &:hover {
    color: #222;
  }
}

.app-modal__body {
  padding: 24px;
  overflow: auto;
  overscroll-behavior: contain;
  flex: 1 1 auto;
}

.app-modal__footer {
  padding: 16px 24px 24px;
  border-top: 1px solid #eee;
  text-align: right;
  flex-shrink: 0;
  background: #fafafa;
}

.app-modal-fade-enter-active,
.app-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.app-modal-fade-enter-from,
.app-modal-fade-leave-to {
  opacity: 0;
}

.app-modal-fade-enter-active .app-modal__dialog,
.app-modal-fade-leave-active .app-modal__dialog {
  transition: transform 0.2s ease;
}

.app-modal-fade-enter-from .app-modal__dialog,
.app-modal-fade-leave-to .app-modal__dialog {
  transform: translateY(8px) scale(0.98);
}
</style>

<style lang="scss">
.app-modal__body {
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1.35rem 0 0.75rem;
    line-height: 1.35;
    color: #2f363a;
    font-weight: 600;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 1.35rem;
  }

  h2 {
    font-size: 1.2rem;
  }

  h3 {
    font-size: 1.05rem;
  }

  p {
    margin: 0 0 0.85rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .app-modal-alert {
    margin-bottom: 16px;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .app-modal-alert--error {
    background: #fdecec;
    color: #8f4d4d;
  }

  .app-modal-alert--success {
    background: #edf7ed;
    color: #2f6b2f;
    font-weight: 600;
  }

  .app-modal-intro {
    margin-bottom: 18px;
    padding-left: 2px;
    color: #666;
    line-height: 1.6;
    font-size: 15px;
  }

  .app-modal-wizard__step-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #666;
  }

  .app-modal-wizard__step--active .app-modal-wizard__step-label {
    color: #2f2f2f;
  }

  .app-modal-wizard__steps {
    position: relative;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 22px;
    padding: 0 4px;
  }

  .app-modal-wizard__steps--4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .app-modal-wizard__steps::before {
    content: '';
    position: absolute;
    top: 15px;
    left: calc(16.666% + 18px);
    right: calc(16.666% + 18px);
    height: 2px;
    background: #e6e6e6;
    z-index: 0;
  }

  .app-modal-wizard__steps--4::before {
    left: calc(12.5% + 14px);
    right: calc(12.5% + 14px);
  }

  @media (max-width: 560px) {
    .app-modal-wizard__steps--4 .app-modal-wizard__step-label {
      font-size: 9px;
      letter-spacing: 0.02em;
    }
  }

  .app-modal-wizard__step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    text-align: center;
    opacity: 0.55;
    appearance: none;
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    cursor: default;
    position: relative;
    z-index: 1;
  }

  .app-modal-wizard__step--clickable:not(:disabled) {
    cursor: pointer;
  }

  .app-modal-wizard__step--clickable:not(:disabled):hover .app-modal-wizard__step-index {
    border-color: #42d9e4;
    color: #42d9e4;
  }

  .app-modal-wizard__step:disabled {
    cursor: default;
  }

  .app-modal-wizard__step--active,
  .app-modal-wizard__step--done {
    opacity: 1;
  }

  .app-modal-wizard__step-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid #d1d1d1;
    font-size: 13px;
    font-weight: 700;
    color: #666;
    background: #fff;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .app-modal-wizard__step-check {
    font-size: 14px;
    line-height: 1;
  }

  .app-modal-wizard__step--done .app-modal-wizard__step-index {
    border-color: #42d9e4;
    background: #42d9e4;
    color: #fff;
  }

  .app-modal-wizard__step--active .app-modal-wizard__step-index {
    border-color: #42d9e4;
    background: #42d9e4;
    color: #fff;
    box-shadow: 0 2px 8px rgba(66, 217, 228, 0.35);
  }

  .app-modal-captcha .frc-content {
    text-align: left;
  }

  .app-modal-captcha .frc-banner {
    display: none;
  }

  .app-modal-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 0;

    &__section {
      padding: 16px 18px;
      background: #fafafa;
      border: 1px solid #ececec;
      border-radius: 12px;
    }

    &__section-title {
      margin: 0 0 14px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #555;
    }

    &__optional {
      margin-left: 6px;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: lowercase;
      color: #999;
    }

    &__grid {
      display: grid;
      gap: 14px 16px;
    }

    &__grid--2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__grid--split {
      grid-template-columns: minmax(0, 140px) minmax(0, 1fr);
    }

    &__grid--compact {
      grid-template-columns: minmax(0, 160px);
    }

    &__field {
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    /* Legacy row/col helpers — reset Bootstrap floats/widths that break CSS grid */
    .row {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      gap: 14px 16px;
      margin: 0;

      &::before,
      &::after {
        content: none;
        display: none;
      }
    }

    [class*='col-'] {
      float: none !important;
      width: auto !important;
      max-width: none;
      min-height: 0;
      padding-left: 0;
      padding-right: 0;
    }

    .col-sm-12 {
      grid-column: span 12;
    }

    .col-sm-8 {
      grid-column: span 8;
    }

    .col-sm-6 {
      grid-column: span 6;
    }

    .col-sm-4 {
      grid-column: span 4;
    }

    .form-group {
      margin: 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    label {
      display: block;
      margin-bottom: 6px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #666;
    }

    .form-control {
      display: block;
      width: 100%;
      border: 1px solid #d8d8d8;
      border-radius: 8px;
      box-shadow: none;
      color: #333;
      font-size: 14px;
      line-height: 1.4;
      height: 44px;
      padding: 10px 14px;
      background: #fff;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &::placeholder {
        color: #aaa;
        opacity: 1;
      }

      &:focus {
        border-color: #42d9e4;
        outline: none;
        box-shadow: 0 0 0 3px rgba(66, 217, 228, 0.15);
      }
    }

    textarea.form-control {
      min-height: 110px;
      height: auto;
      resize: vertical;
      padding-top: 12px;
    }

    input[type="date"].form-control,
    input[type="number"].form-control {
      appearance: none;
      -webkit-appearance: none;
    }

    input[type="date"].form-control::-webkit-calendar-picker-indicator {
      cursor: pointer;
      opacity: 0.65;
    }

    input[type="number"].form-control {
      -moz-appearance: textfield;
    }

    input[type="number"].form-control::-webkit-outer-spin-button,
    input[type="number"].form-control::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    .app-modal-form__actions {
      margin-top: 8px;
    }

    .app-modal-captcha {
      margin: 4px 0 0;
    }

    .button {
      width: 100%;
      margin-top: 0;
    }

    @media (max-width: 560px) {
      &__section {
        padding: 14px;
      }

      &__grid--2,
      &__grid--split {
        grid-template-columns: 1fr;
      }

      &__grid--compact {
        grid-template-columns: 1fr;
      }

      .col-sm-6,
      .col-sm-4,
      .col-sm-8 {
        grid-column: span 12;
      }
    }

    &--plain {
      padding: 0;
      background: transparent;
      border: none;
    }
  }

  .button:disabled {
    background: #d0d0d0 !important;
    pointer-events: none;
    opacity: 0.85;
  }
}
</style>
